/**
 * Native-shareable certificate pride card (canvas) — name, ID, verify link.
 */

import { canvasToPngFile, shareNative, type NativeShareResult } from "./nativeShare";
import { viralShareFooter } from "./shareCopy";

export interface ShareCertificateInput {
  studentName: string;
  certificateId: string;
  course: string;
  issuedAtIso: string;
  accuracyPct: number | null;
  examsVerified: number;
  verificationUrl: string;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

export function renderShareCertificateCard(input: ShareCertificateInput): HTMLCanvasElement {
  const W = 1080;
  const H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;

  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#071428");
  bg.addColorStop(0.5, "#0c2a4a");
  bg.addColorStop(1, "#1a1408");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  const spot = ctx.createRadialGradient(W * 0.5, 200, 20, W * 0.5, 320, 560);
  spot.addColorStop(0, "rgba(255,213,106,0.3)");
  spot.addColorStop(1, "rgba(255,213,106,0)");
  ctx.fillStyle = spot;
  ctx.fillRect(0, 0, W, 720);

  ctx.strokeStyle = "rgba(255,213,106,0.55)";
  ctx.lineWidth = 4;
  roundRect(ctx, 48, 48, W - 96, H - 96, 28);
  ctx.stroke();
  ctx.strokeStyle = "rgba(255,246,232,0.12)";
  ctx.lineWidth = 1;
  roundRect(ctx, 64, 64, W - 128, H - 128, 22);
  ctx.stroke();

  ctx.fillStyle = "rgba(255,213,106,0.95)";
  ctx.font = "600 26px Georgia, serif";
  ctx.textAlign = "center";
  ctx.fillText("ULTIMATE CLASSIC PIANO TUTOR", W / 2, 120);

  ctx.fillStyle = "rgba(255,246,232,0.55)";
  ctx.font = "500 22px system-ui, sans-serif";
  ctx.fillText("OFFICIAL COURSE CERTIFICATE", W / 2, 160);

  ctx.fillStyle = "#fff6e8";
  ctx.font = "700 58px Georgia, serif";
  const name = (input.studentName || "Piano Student").trim().slice(0, 28);
  ctx.fillText(name, W / 2, 260);

  ctx.fillStyle = "rgba(255,246,232,0.75)";
  ctx.font = "italic 28px Georgia, serif";
  ctx.fillText("has completed the full classical course", W / 2, 320);

  roundRect(ctx, 120, 370, W - 240, 280, 20);
  ctx.fillStyle = "rgba(0,0,0,0.35)";
  ctx.fill();
  ctx.strokeStyle = "rgba(255,213,106,0.4)";
  ctx.lineWidth = 2;
  ctx.stroke();

  const issued = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(input.issuedAtIso));

  const rows: Array<[string, string]> = [
    ["COURSE", input.course.slice(0, 40)],
    ["CERTIFICATE ID", input.certificateId.slice(0, 28)],
    ["ISSUED", issued],
    [
      "VERIFIED EXAMS",
      `${input.examsVerified}${input.accuracyPct != null ? ` · ${input.accuracyPct.toFixed(0)}% avg` : ""}`,
    ],
  ];
  rows.forEach(([k, v], i) => {
    const y = 420 + i * 55;
    ctx.fillStyle = "rgba(255,213,106,0.85)";
    ctx.font = "600 18px system-ui, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(k, 160, y);
    ctx.fillStyle = "#fff6e8";
    ctx.font = "600 26px Georgia, serif";
    ctx.fillText(v, 160, y + 28);
  });

  ctx.textAlign = "center";
  ctx.fillStyle = "rgba(255,246,232,0.9)";
  ctx.font = "italic 26px Georgia, serif";
  ctx.fillText("Verify authenticity · share your milestone", W / 2, 720);

  roundRect(ctx, 100, 760, W - 200, 200, 18);
  ctx.fillStyle = "rgba(255,213,106,0.12)";
  ctx.fill();
  ctx.strokeStyle = "rgba(255,213,106,0.55)";
  ctx.stroke();

  ctx.fillStyle = "#ffd56a";
  ctx.font = "700 28px system-ui, sans-serif";
  ctx.fillText("SCAN OR OPEN TO VERIFY", W / 2, 810);
  ctx.fillStyle = "#9bffd8";
  ctx.font = "500 22px system-ui, sans-serif";
  const verifyShort = input.verificationUrl.replace(/^https?:\/\//, "").slice(0, 52);
  ctx.fillText(verifyShort, W / 2, 855);
  ctx.fillStyle = "#f2f8ff";
  ctx.font = "600 20px system-ui, sans-serif";
  ctx.fillText("Learn piano easily · free on Play & Microsoft Store", W / 2, 900);
  ctx.fillStyle = "#4dffb0";
  ctx.font = "700 18px system-ui, sans-serif";
  ctx.fillText("Share & Earn · invite friends · earn rewards", W / 2, 935);
  ctx.fillStyle = "#8ad6ff";
  ctx.font = "600 18px system-ui, sans-serif";
  ctx.fillText("play.google.com + apps.microsoft.com/detail/9P9WSTJSPC2G", W / 2, 968);

  ctx.fillStyle = "rgba(255,246,232,0.45)";
  ctx.font = "500 18px system-ui, sans-serif";
  ctx.fillText("LOTI Games and Apps · SHRIKANT & SUMNNA BHARDWAJ", W / 2, 1240);

  return canvas;
}

export function buildCertificateShareText(input: ShareCertificateInput): string {
  const name = input.studentName.trim() || "I";
  return (
    `${name} earned an official Ultimate Classic Piano Tutor certificate!\n` +
    `ID: ${input.certificateId}\n` +
    `Course: ${input.course}\n` +
    `Verify: ${input.verificationUrl}\n\n` +
    viralShareFooter()
  );
}

export async function shareCertificateCard(
  input: ShareCertificateInput,
): Promise<NativeShareResult> {
  const canvas = renderShareCertificateCard(input);
  const file = await canvasToPngFile(canvas, `upt-certificate-${input.certificateId}.png`);
  return shareNative({
    title: "My Piano Certificate",
    text: buildCertificateShareText(input),
    url: input.verificationUrl,
    file,
    downloadName: `upt-certificate-${input.certificateId}.png`,
  });
}
